import { useState } from 'react';

import { usePipelineJobs, useSubmitPipelineJob } from 'src/api/queries';
import { JobQueue } from 'src/components/editorial/JobQueue';
import { StagePicker } from 'src/components/editorial/StagePicker';
import { buildJobView } from 'src/lib/jobBoard';
import { Button } from 'src/components/ui/Button';
import { Icon } from 'src/components/ui/Icon';
import { activeJob, completedStages, describeSubmitFailure, nextStage, stagesToResume, STAGE_LABEL } from 'src/lib/pipelineStages';
import type { EditorialStreamSummary, PipelineStage } from 'src/types/editorial';
import { PIPELINE_STAGES } from 'src/types/editorial';

type ContinueProcessingProps = {
  streamId: string;
  /** What this broadcast already produced — what makes "continuar" mean the right thing. */
  summary: EditorialStreamSummary;
};

/**
 * Run the sections this broadcast is missing, without paying for the ones it already has.
 *
 * A run that stopped at the prenotas is not a failed run — it is a broadcast waiting for someone
 * to decide the stories are worth the paid stages. This is where that decision gets made, and it
 * is the reason the stage picker on the submission form is not a trap.
 *
 * The stages already done are read from the artifacts, not from the job history: a stream
 * processed before the queue existed, or from the command line, has no job and its transcript is
 * just as real.
 */
export function ContinueProcessing({ streamId, summary }: ContinueProcessingProps) {
  const done = completedStages(summary);
  const missing = nextStage(done);

  const jobs = usePipelineJobs({ streamId, limit: 5 });
  const submit = useSubmitPipelineJob();
  // Held as "what the editor picked", not as "the stop point", so the default keeps following the
  // broadcast: after a run finishes, the next missing stage becomes the suggestion without
  // overriding a choice already made on screen.
  const [picked, setPicked] = useState<PipelineStage | null>(null);
  const stopAfter = picked ?? missing ?? 'notas';
  // The path line under the chips: the picked stage plus every stage before it, in run order.
  const rerunNames = PIPELINE_STAGES.slice(0, PIPELINE_STAGES.indexOf(stopAfter) + 1).map(stage => STAGE_LABEL[stage]);
  const [queuedId, setQueuedId] = useState('');

  const running = activeJob((jobs.data?.jobs ?? []).filter(job => job.status === 'pending' || job.status === 'running'));

  return (
    <section className="rerun-block">
      <h2 className="section-title">Reprocesamiento</h2>

      {running ? (
        <>
          <p className="continue__note">
            Ya hay una corrida en curso para esta transmisión. Cuando termine podrás pedir las siguientes etapas.
          </p>
          <JobQueue views={[buildJobView(running, Date.now())]} emptyNote="" />
        </>
      ) : (
        <details className="rerun">
          <summary>
            <span className="rerun__icon" aria-hidden="true">
              <Icon name="replay" />
            </span>
            <span className="rerun__summary">
              Volver a correr etapas de esta transmisión
              <small>
                {missing
                  ? `Esta transmisión llegó hasta ${lastDone(done)}. Sigue ${STAGE_LABEL[missing]}. Los resultados de cada etapa se reemplazan, junto con los de todas las que le preceden.`
                  : 'Los resultados de cada etapa se reemplazan, junto con los de todas las que le preceden.'}
              </small>
            </span>
            <span className="rerun__chevron" aria-hidden="true">
              <Icon name="expandMore" />
            </span>
          </summary>
          <div className="rerun__body">
            <StagePicker chips value={stopAfter} onChange={setPicked} done={done} disabled={submit.isPending} />
            <p className="rerun-path" aria-live="polite">
              Se recorrerán de nuevo <strong>{rerunNames.length} {rerunNames.length === 1 ? 'etapa' : 'etapas'}</strong>:{' '}
              {rerunNames.join(' → ')}.
            </p>
            <div className="process-form__actions">
              <Button
                variant="primary"
                busy={submit.isPending}
                onClick={() =>
                  submit.mutate(
                    { streamId, stages: stagesToResume(stopAfter, done, summary.criteriaEvaluated ?? 0) },
                    { onSuccess: result => setQueuedId(result.jobId) }
                  )
                }
              >
                <Icon name="replay" /> Procesar hasta {STAGE_LABEL[stopAfter]}
              </Button>
              {submit.isError && <SubmitFailure error={submit.error} />}
              {queuedId && <p className="voice-actions__ok">En cola ({queuedId}).</p>}
            </div>
          </div>
        </details>
      )}
    </section>
  );
}

function SubmitFailure({ error }: { error: unknown }) {
  const { headline, detail, isAlreadyQueued } = describeSubmitFailure(error);

  return (
    <p className={isAlreadyQueued ? 'voice-actions__note' : 'voice-actions__error'}>
      <strong>{headline}.</strong> {detail}
    </p>
  );
}

function lastDone(done: ReadonlySet<PipelineStage>): string {
  const stages = [...done];
  const last = stages[stages.length - 1];
  return last ? STAGE_LABEL[last] : 'la captura';
}
